import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Gift, SlidersHorizontal } from 'lucide-react';
import { useState } from 'react';
import { useForm } from 'react-hook-form';
import { toast } from 'sonner';
import { loyaltyAdjustInput } from '@barbeiropro/contracts';
import { ConfirmDialog } from '@/components/shared/confirm-dialog';
import { Field } from '@/components/shared/field';
import { FormModal } from '@/components/shared/form-modal';
import { ErrorState, LoadingState } from '@/components/shared/states';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { api, errorMessage } from '@/lib/api';
import { fmtDateTime } from '@/lib/format';
import { useTimezone } from '@/lib/org';
import { useSession } from '@/lib/session';
import type { CustomerLoyalty } from './api';
import { applyServerError, zodForm } from './lib';

const KIND: Record<string, string> = { earn: 'Ganho', redeem: 'Resgate', adjust: 'Ajuste manual', reverse: 'Estorno' };

function AdjustModal({ customerId, open, onOpenChange }: { customerId: string; open: boolean; onOpenChange: (v: boolean) => void }) {
  const qc = useQueryClient();
  interface Values {
    points: string;
    reason: string;
  }
  const norm = (v: Values) => ({ points: Number(v.points), reason: v.reason });
  const form = useForm<Values>({ resolver: zodForm<Values>(loyaltyAdjustInput, norm), defaultValues: { points: '', reason: '' } });
  const { errors } = form.formState;
  const save = useMutation({
    mutationFn: (v: Values) => api.post(`/customers/${customerId}/loyalty/adjust`, loyaltyAdjustInput.parse(norm(v))),
    onSuccess: () => {
      toast.success('Saldo ajustado');
      qc.invalidateQueries({ queryKey: ['customers'] });
      form.reset();
      onOpenChange(false);
    },
    onError: (e) => applyServerError(form, e),
  });
  return (
    <FormModal
      open={open}
      onOpenChange={onOpenChange}
      size="sm"
      title="Ajustar pontos"
      description="Use número negativo para tirar pontos. O motivo fica registrado na auditoria."
      footer={
        <>
          <Button variant="outline" type="button" onClick={() => onOpenChange(false)}>
            Voltar
          </Button>
          <Button type="submit" form="adjust-form" disabled={save.isPending}>
            {save.isPending ? 'Salvando…' : 'Aplicar ajuste'}
          </Button>
        </>
      }
    >
      <form id="adjust-form" className="grid gap-4" noValidate onSubmit={form.handleSubmit((v) => save.mutate(v))}>
        <Field id="adj-points" label="Pontos (+ ou −)" error={errors.points?.message}>
          <Input id="adj-points" inputMode="numeric" placeholder="Ex.: 5 ou -3" {...form.register('points')} />
        </Field>
        <Field id="adj-reason" label="Motivo" error={errors.reason?.message}>
          <Input id="adj-reason" maxLength={200} {...form.register('reason')} />
        </Field>
      </form>
    </FormModal>
  );
}

export function CustomerLoyaltyTab({ customerId }: { customerId: string }) {
  const qc = useQueryClient();
  const tz = useTimezone();
  const { can } = useSession();
  const [adjustOpen, setAdjustOpen] = useState(false);
  const q = useQuery({ queryKey: ['customers', 'loyalty', customerId], queryFn: () => api.get<CustomerLoyalty>(`/customers/${customerId}/loyalty`) });

  const redeem = async () => {
    try {
      const r = await api.post<{ balance: number; reward: string }>(`/customers/${customerId}/loyalty/redeem`, {});
      toast.success(`Recompensa resgatada: ${r.reward}`);
      qc.invalidateQueries({ queryKey: ['customers'] });
    } catch (e) {
      toast.error(errorMessage(e));
      throw e;
    }
  };

  if (q.isLoading) return <LoadingState />;
  if (q.error || !q.data) return <ErrorState error={q.error} onRetry={() => q.refetch()} />;
  const l = q.data;
  const pct = Math.min(100, Math.round((Math.max(0, l.balance) / l.program.rewardThreshold) * 100));
  const unit = l.program.mode === 'points' ? 'pontos' : 'visitas';

  return (
    <div className="grid gap-5">
      {!l.program.active && (
        <p className="rounded-lg border border-brass/40 bg-brass/10 px-4 py-3 text-sm">
          O programa de fidelidade está desativado, então novos pagamentos não geram {unit}. O saldo abaixo é o que o cliente já acumulou.
        </p>
      )}
      <div className="flex flex-wrap items-end justify-between gap-4">
        <div>
          <p className="text-xs text-muted-foreground">Saldo atual</p>
          <p className="font-display text-5xl leading-none tabular">
            {l.balance} <span className="text-lg font-normal text-muted-foreground">{unit}</span>
          </p>
          <p className="mt-2 text-sm text-muted-foreground">
            Recompensa: {l.program.rewardDescription} · {l.program.rewardThreshold} {unit}
          </p>
        </div>
        <div className="flex flex-wrap gap-2">
          {can('customers.write') && (
            <ConfirmDialog
              destructive={false}
              trigger={
                <Button disabled={!l.canRedeem}>
                  <Gift aria-hidden /> Resgatar recompensa
                </Button>
              }
              title="Resgatar recompensa?"
              description={`Serão descontados ${l.program.rewardThreshold} ${unit} do saldo para: ${l.program.rewardDescription}.`}
              confirmLabel="Resgatar"
              onConfirm={redeem}
            />
          )}
          {can('loyalty.manage') && (
            <Button variant="outline" onClick={() => setAdjustOpen(true)}>
              <SlidersHorizontal aria-hidden /> Ajustar pontos
            </Button>
          )}
        </div>
      </div>
      <div
        role="progressbar"
        aria-valuenow={pct}
        aria-valuemin={0}
        aria-valuemax={100}
        aria-label="Progresso até a recompensa"
        className="h-2 w-full max-w-xl overflow-hidden rounded-full bg-muted"
      >
        <div className="h-full rounded-full bg-primary" style={{ width: `${pct}%` }} />
      </div>

      <section aria-labelledby="loy-tx">
        <h2 id="loy-tx" className="mb-2 text-sm font-semibold">
          Movimentações
        </h2>
        {l.transactions.length === 0 ? (
          <p className="text-sm text-muted-foreground">Nenhuma movimentação ainda.</p>
        ) : (
          <ul className="divide-y rounded-lg border bg-card">
            {l.transactions.map((x) => (
              <li key={x.id} className="flex flex-wrap items-center gap-x-4 gap-y-1 px-4 py-3 text-sm">
                <span className="tabular w-36 text-muted-foreground">{fmtDateTime(x.createdAt, tz)}</span>
                <span className="w-28 font-medium">{KIND[x.kind] ?? x.kind}</span>
                <span className="min-w-0 flex-1 truncate text-muted-foreground">{x.reason ?? ''}</span>
                <span className={`tabular w-16 text-right font-medium ${x.points < 0 ? 'text-oxblood-ink' : ''}`}>{x.points > 0 ? `+${x.points}` : x.points}</span>
              </li>
            ))}
          </ul>
        )}
      </section>
      <AdjustModal customerId={customerId} open={adjustOpen} onOpenChange={setAdjustOpen} />
    </div>
  );
}
